import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useContext, useState } from 'react';
import { dismissSyncStatus, getSyncStatus } from '../rest/BackendApi';
import { useRecursiveTimeout } from '../hooks/useInterval';
import { getExponentialBackoffDelay } from '../util/DelayUtil';
import UserContext from '../UserContext';
import { AlertColor } from '@mui/material/Alert/Alert';

function OrgSyncStatusDisplay(props: any) {
  const { syncReadyCallback } = props;
  const userContext = useContext(UserContext);

  const [inProgress, setInProgress] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [delay, setDelay] = useState<number | null>(500);
  const [attempt, setAttempt] = useState(1);
  const maxAttempts = 30;

  const getNonSuccessSyncStatusBox = (syncStatus: SyncStatus) => {
    async function dismiss(id: number) {
      await dismissSyncStatus(id);
      setSyncStatus(null);
    }

    return (
      <Alert severity={getSyncStatusSeverity(syncStatus.result)} onClose={() => dismiss(syncStatus.id)}>
        {syncStatus.message}
      </Alert>
    );
  };

  useRecursiveTimeout(async () => {
    const loadSyncStatus = async () => {
      let syncStatus = await getSyncStatus('Org');
      if (syncStatus?.result === 'InProgress') {
        if (attempt >= maxAttempts) {
          setInProgress(false);
          setDelay(null);
        } else {
          setInProgress(true);
          setAttempt(attempt + 1);
          setDelay(getExponentialBackoffDelay(attempt, 500));
        }
      } else {
        if (syncStatus != null && userContext.refreshUser) {
          userContext.refreshUser();
        }
        setInProgress(false);
        setDelay(null);
        setSyncStatus(syncStatus);
        syncReadyCallback(syncStatus, attempt);
      }
    };
    await loadSyncStatus();
  }, delay);

  if (inProgress) {
    return (
      <Box paddingBottom="10px">
        <Alert severity="info" sx={{ '.MuiAlert-message': { width: '100%', overflow: 'hidden' } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <span>Syncing your organisations with Xero. The page updates when it's done.</span>
            <span>
              <CircularProgress size="18px" color="info" />
            </span>
          </Box>
        </Alert>
      </Box>
    );
  } else if (syncStatus && syncStatus?.result != 'Success') {
    return <Box paddingBottom="10px">{getNonSuccessSyncStatusBox(syncStatus)}</Box>;
  }
}

const getSyncStatusSeverity = (syncResult: SyncResult): AlertColor => {
  switch (syncResult) {
    case 'Error':
      return 'error';
    case 'Partial':
      return 'warning';
    case 'LimitReached':
      return 'warning';
    default:
      return 'info';
  }
};

export default React.memo(OrgSyncStatusDisplay);
